import { Box } from '@mui/material';
import type { FC } from 'react';
import { BrazeBannerPlacement } from '~/components/molecules/BrazeBannerPlacement';
import { BRAZE_PLACEMENT_ID } from '~/constants/braze';
import { useBrazeBanners } from '~/hooks/useBrazeBanners';
import type { BrazeBanner } from '~/lib/braze.client';

const [placementId] = BRAZE_PLACEMENT_ID.ACCOUNT_SIDE;

// Stable reference so disabling the banner doesn't change useBrazeBanners's
// effect dependency identity on every render.
const NO_PLACEMENT_IDS: readonly string[] = [];

export type AccountSidebarBannerProps = {
  /** Whether the banner is currently visible - gates the Braze banner fetch. */
  enabled?: boolean;
  maxWidth?: number;
  aspectRatio?: string;
  /**
   * The `account_side` banner, when the caller already fetched it as part of a combined
   * `useBrazeBanners` call (e.g. `AccountContainer`, which requests this placement together
   * with its top banner in a single Braze request) - pass `null` if that fetch resolved with
   * no banner. Omit this prop to have the component fetch `account_side` on its own instead,
   * for standalone use (e.g. the mobile nav drawer's Account submenu).
   */
  fetchedBanner?: BrazeBanner | null;
};

/**
 * Renders the Braze `account_side` promo banner, scaled to fit its
 * container (via `maxWidth` + `aspectRatio`) instead of a fixed box so it
 * never overflows at narrower widths. Shared between the desktop account
 * sidebar and the mobile nav drawer's expanded Account submenu.
 */
export const AccountSidebarBanner: FC<AccountSidebarBannerProps> = ({
  enabled = true,
  maxWidth = 282,
  aspectRatio = '282 / 600',
  fetchedBanner,
}) => {
  const shouldSelfFetch = fetchedBanner === undefined;
  const { banners: selfFetchedBanners } = useBrazeBanners(
    shouldSelfFetch && enabled ? BRAZE_PLACEMENT_ID.ACCOUNT_SIDE : NO_PLACEMENT_IDS,
  );
  const banner = shouldSelfFetch ? selfFetchedBanners[0] : fetchedBanner;

  if (!enabled || !banner) {
    return null;
  }

  return (
    <Box
      data-testid="braze-banner-account-sidebar"
      data-promo-placement={placementId}
      sx={{ width: '100%', maxWidth, aspectRatio, borderRadius: '16px', overflow: 'hidden' }}
    >
      <BrazeBannerPlacement placementId={placementId} />
    </Box>
  );
};
